import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../../../components/button'
import { Input } from '../../../../components/input'
import { Loader } from '../../../../components/loader'
import { PageContainer } from '../../../../components/page-container/page-container'
import { showToast } from '../../../../components/sonner'
// import { ErrorDisplay } from '../../../../components/error-display/error-display'
import { Eye } from '../../../../components/icons/eye'
import { EyeClosed } from '../../../../components/icons/eye-closed'
import { useMask } from '../../../../hooks/useMask'
import type { RootState } from '../../../../redux/global-store'
import { paths } from '../../../../router/paths'
import { formatDPI } from '../../../../utils/masks/formatDPI'
import { isJourneyActive } from '../../../../utils/journey-utils'
import {
  loginRequest,
  loginReset,
} from '../../presentation/redux/actions/login-actions'

export const Login = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { user, load, error } = useSelector(
    (state: RootState) => state.auth.login,
  )

  const { journey, load: journeyLoad, error: journeyError } = useSelector(
    (state: RootState) => state.journey.journey,
  )

  const [taxId, setTaxId] = useMask(formatDPI)
  const [pin, setPin] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [rememberDpi, setRememberDpi] = useState(false)

  // Função para permitir somente números
  const handleNumericInput = (value: string, setter: (value: string) => void) => {
    const numericValue = value.replace(/[^0-9]/g, '')
    setter(numericValue)
  }

  const isValidTaxId = taxId.replaceAll('-', '').length === 13
  const isValidPin = pin.length > 0

  const handleLogin = () => {
    const rawDpi = taxId.replaceAll('-', '')
    if (rememberDpi && rawDpi.length === 13) {
      localStorage.setItem('rememberedDpi', rawDpi)
    } else {
      localStorage.removeItem('rememberedDpi')
    }
    dispatch(loginRequest({ dpi: rawDpi, password: pin }))
  }

  useEffect(() => {
    if (error !== '') {
      // Mostra toast com mensagem amigável
      showToast(error, 'error')
      dispatch(loginReset({ resetAll: false }))
    }
  }, [error])

  useEffect(() => {
    if (user && !load && !journeyLoad) {
      if (journeyError) {
        showToast('Error al verificar journey: ' + journeyError, 'error')
        return
      }

      // Verifica se o usuário está logado
      const token = localStorage.getItem('token')
      const isLogged = sessionStorage.getItem('logged') === 'true'

      if (token && isLogged) {
        // Se tem journey ativa (status 0), vai para home. Se não tem ou está finalizada (status 1), vai para tela de input
        if (isJourneyActive(journey)) {
          navigate(paths.home)
        } else {
          navigate(paths.journey.input)
        }
      }
    }
  }, [user, load, journey, journeyLoad, journeyError, navigate])

  // Carrega DPI salvo
  useEffect(() => {
    const saved = localStorage.getItem('rememberedDpi')
    if (saved && saved.length === 13) {
      setTaxId(saved)
      setRememberDpi(true)
    }
  }, [])

  return (
    <PageContainer type="unauthenticated">
      {load && <Loader />}
      <div className="flex w-full flex-col items-center gap-8 px-8">
        <p className="text-2xl font-normal">Nos alegra verte de nuevo</p>
        <div className="flex w-full flex-col gap-2 pt-8">
          <p className="text-lg font-normal">Inicia sesión en tu cuenta</p>

          {/* Exibição de erro inline */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          <div className="flex flex-col gap-10">
            <Input
              onChange={e => handleNumericInput(e.target.value, setTaxId)}
              value={taxId}
              placeholder="DPI"
              maxLength={15}
              inputMode="numeric"
            />
            <Input
              onChange={e => handleNumericInput(e.target.value, setPin)}
              value={pin}
              type={showPassword ? 'text' : 'password'}
              placeholder="PIN"
              rightIcon={showPassword ? <EyeClosed /> : <Eye />}
              onRightIconClick={() => setShowPassword(!showPassword)}
              inputMode="numeric"
              maxLength={4}
            />
          </div>
          <div className="flex w-full items-center justify-between px-1 pt-2">
            <div className="flex items-center gap-2">
              <input
                id="remember-dpi"
                type="checkbox"
                checked={rememberDpi}
                onChange={e => setRememberDpi(e.target.checked)}
                className="h-4 w-4 cursor-pointer accent-[#2D4990]"
              />
              <label
                htmlFor="remember-dpi"
                className="cursor-pointer select-none text-sm font-normal"
              >
                Recordar DPI
              </label>
            </div>
            <p
              onClick={() => navigate(paths.user.resetPassword.informTaxId)}
              className="cursor-pointer text-sm font-normal hover:underline"
            >
              Olvide mi PIN
            </p>
          </div>
        </div>
        <div className="flex w-full flex-col items-center pt-6">
          <Button
            disabled={!isValidTaxId || !isValidPin}
            className="h-[55px] w-full rounded-[10px] text-xl font-semibold"
            onClick={handleLogin}
          >
            Ingresar
          </Button>
        </div>
        <div className="flex w-full flex-col items-center pt-4">
          <Button
            variant="outlined"
            className="h-[55px] w-full rounded-[10px] text-xl font-semibold"
            onClick={() => navigate(paths.authentication.welcome)}
          >
            Regresar
          </Button>
        </div>
        <div className="py-8">
          <p className="text-sm font-normal">
            ¿No tienes una cuenta?{' '}
            <span
              onClick={() => navigate(paths.user.createUser)}
              className="cursor-pointer font-normal text-[#2D4990] hover:underline"
            >
              Regístrate
            </span>
          </p>
        </div>
      </div>
    </PageContainer>
  )
}
